import { useEffect, useState, type ReactNode } from 'react';
import type { DashboardPeriodPreset, DashboardResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { EInvoiceStatusCard } from './EInvoiceSetup';
import DashboardBody, { type DashboardTarget } from './DashboardBody';
import DashboardSettings from './DashboardSettings';
import { Icon } from './icons';
import { greeting, shortDate } from './dashboardText';
import './dashboard.css';

export type { DashboardTarget } from './DashboardBody';

interface Props {
  companyId: string;
  currency: string;
  userName: string;
  /** بلا `report.read` لا رقم ولا تنبيه — التحية وحدها. */
  canReadReports: boolean;
  canManageSettings: boolean;
  inventoryEnabled: boolean;
  onNavigate: (target: DashboardTarget) => void;
  onSessionLost: () => void;
}

const PRESETS: { value: DashboardPeriodPreset; label: string }[] = [
  { value: 'month', label: 'هذا الشهر' },
  { value: 'quarter', label: 'الربع' },
  { value: 'ytd', label: 'منذ بداية السنة' },
  { value: 'custom', label: 'مخصص' },
];

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

function Skeleton() {
  return (
    <div className="dash-skeleton" aria-busy="true" role="status">
      <span className="sr-only">جارى تحميل اللوحة…</span>
      <div className="dash-kpis">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="dash-kpi dash-shimmer" />
        ))}
      </div>
      <div className="dash-row">
        <div className="dash-panel dash-shimmer tall" />
        <div className="dash-panel dash-shimmer tall" />
      </div>
    </div>
  );
}

export function periodCaption(period: DashboardResponse['period']): string {
  return (
    shortDate(period.from) +
    ' — ' +
    shortDate(period.to) +
    ' • مقارنة ب' +
    (period.comparison === 'same_period_last_year'
      ? 'نفس الفترة من العام الماضى'
      : 'الفترة السابقة') +
    ' (' +
    shortDate(period.previousFrom) +
    ' — ' +
    shortDate(period.previousTo) +
    ')'
  );
}

/**
 * اللوحة الرئيسية الذكية: مؤشرات الفترة مع المقارنة، ورسوم، وتنبيهات بقواعد على الخادم،
 * ومهام اليوم. هنا الفترة والجلب والحالات؛ العرض كله فى `DashboardBody`.
 */
export default function Dashboard({
  companyId,
  currency,
  userName,
  canReadReports,
  canManageSettings,
  inventoryEnabled,
  onNavigate,
  onSessionLost,
}: Props) {
  const [asOf, setAsOf] = useState(today);
  const [preset, setPreset] = useState<DashboardPeriodPreset>('month');
  const [customFrom, setCustomFrom] = useState(() => today().slice(0, 8) + '01');
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!canReadReports) return;
    let cancelled = false;
    setFailure(null);
    setLoading(true);
    api
      .dashboard(companyId, asOf, preset, customFrom)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) {
          onSessionLost();
          return;
        }
        setFailure(
          caught instanceof ProblemError
            ? (caught.problem.detail ?? caught.problem.title)
            : caught instanceof Error
              ? caught.message
              : String(caught),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, asOf, preset, customFrom, canReadReports, onSessionLost, reload]);

  const firstName = userName.trim().split(/\s+/)[0] ?? '';
  const hello = greeting(new Date().getHours()) + (firstName ? '، ' + firstName : '');

  const hero = (
    <DashboardHero
      hello={hello}
      caption={data ? periodCaption(data.period) : null}
      controls={
        canReadReports && (
          <PeriodControls
            preset={preset}
            onPreset={setPreset}
            asOf={asOf}
            onAsOf={setAsOf}
            customFrom={customFrom}
            onCustomFrom={setCustomFrom}
          />
        )
      }
    />
  );

  if (!canReadReports) {
    return (
      <div className="dash">
        {hero}
        <section className="dash-empty">
          <h2>مرحبًا بك</h2>
          <p>
            أرقام اللوحة وتنبيهاتها تظهر لمن له صلاحية قراءة التقارير. اطلب من مالك الشركة إضافتها
            لدورك إن كنت تحتاجها.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="dash">
      {hero}

      {failure && <p className="error">تعذّر تحميل اللوحة: {failure}</p>}
      {!data && !failure && <Skeleton />}

      {data && data.isEmpty && (
        <section className="dash-empty">
          <h2>دفاترك جاهزة — ابدأ أول خطوة</h2>
          <p>اللوحة تمتلئ بالمؤشرات والرسوم والتنبيهات بمجرد نشر أول مستند.</p>
          <div className="dash-empty-actions">
            <button type="button" onClick={() => onNavigate({ kind: 'customers' })}>
              أضف عميلاً
            </button>
            <button type="button" onClick={() => onNavigate({ kind: 'invoices' })}>
              أصدر فاتورة
            </button>
            <button type="button" onClick={() => onNavigate({ kind: 'expenses' })}>
              سجّل مصروفًا
            </button>
            <button type="button" className="ghost" onClick={() => onNavigate({ kind: 'periods' })}>
              الفترات المالية
            </button>
          </div>
        </section>
      )}

      {data && !data.isEmpty && (
        <DashboardBody
          data={data}
          currency={currency}
          inventoryEnabled={inventoryEnabled}
          loading={loading}
          onNavigate={onNavigate}
          insightsAction={
            canManageSettings && (
              <button
                type="button"
                className="ghost dash-icon-button"
                aria-expanded={showSettings}
                onClick={() => setShowSettings((open) => !open)}
              >
                <Icon name="settings" size={16} /> الحدود
              </button>
            )
          }
          insightsTop={
            showSettings && (
              <DashboardSettings
                companyId={companyId}
                onClose={() => setShowSettings(false)}
                onSaved={() => {
                  setShowSettings(false);
                  setReload((value) => value + 1);
                }}
                onSessionLost={onSessionLost}
              />
            )
          }
          eInvoiceCard={
            <EInvoiceStatusCard
              companyId={companyId}
              onOpen={() => onNavigate({ kind: 'settings', tab: 'einvoice' })}
            />
          }
        />
      )}
    </div>
  );
}

export function DashboardHero({
  hello,
  caption,
  controls,
}: {
  hello: string;
  caption: string | null;
  controls?: ReactNode;
}) {
  return (
    <header className="dash-hero">
      <div className="dash-hello">
        <p className="dash-greet">{hello}</p>
        <h1>الرئيسية</h1>
        {caption && <p className="dash-caption">{caption}</p>}
      </div>
      {controls}
    </header>
  );
}

export function PeriodControls({
  preset,
  onPreset,
  asOf,
  onAsOf,
  customFrom,
  onCustomFrom,
}: {
  preset: DashboardPeriodPreset;
  onPreset: (preset: DashboardPeriodPreset) => void;
  asOf: string;
  onAsOf: (date: string) => void;
  customFrom: string;
  onCustomFrom: (date: string) => void;
}) {
  return (
    <div className="dash-controls">
      <div className="dash-segmented" role="radiogroup" aria-label="الفترة">
        {PRESETS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={preset === option.value}
            className={preset === option.value ? 'is-active' : undefined}
            onClick={() => onPreset(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="dash-dates">
        {preset === 'custom' && (
          <label>
            <span>من</span>
            <input
              type="date"
              value={customFrom}
              max={asOf}
              onChange={(event) => event.target.value && onCustomFrom(event.target.value)}
            />
          </label>
        )}
        <label>
          <span>حتى</span>
          <input
            type="date"
            value={asOf}
            onChange={(event) => event.target.value && onAsOf(event.target.value)}
          />
        </label>
      </div>
    </div>
  );
}
